/* Panier maison MLP : moteur des paliers cadeaux, fonctions PURES (aucun accès au DOM ni au réseau).
   Testé sous Node : node --test theme-modules/panier-maison/tests
   Entrées : le panier de /cart.js, la config rendue par la section (Liquid), la mémoire liée au jeton du panier.
   Un cadeau se reconnaît au code Corner de son palier porté par la ligne (_corner_promo_code), jamais au type produit. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MLPCartEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // « USD:95, GBP:72 » -> { USD: 95, GBP: 72 } ; séparateurs virgule, point-virgule ou ligne, décimale = point
  function parseTable(text) {
    const out = {};
    String(text || '').split(/[,\n;]/).forEach((part) => {
      const m = part.trim().match(/^([A-Za-z]{3})\s*:\s*([0-9]+(?:[.,][0-9]+)?)$/);
      if (m) out[m[1].toUpperCase()] = Number(m[2].replace(',', '.'));
    });
    return out;
  }

  // Seuil en cents de la devise de la cliente : prix fixe de la table d'abord, sinon euros x taux, arrondi à l'unité supérieure.
  function thresholdCents(eur, fixed, currency, shopCurrency, rate) {
    const table = typeof fixed === 'object' && fixed ? fixed : parseTable(fixed);
    if (table[currency] != null) return Math.round(table[currency] * 100);
    if (eur == null || eur === '') return null;
    if (currency === shopCurrency) return Math.round(Number(eur) * 100);
    const r = Number(rate);
    if (!(r > 0)) return null; // devise inconnue sans taux : on ne promet rien
    return Math.ceil(Number(eur) * r) * 100;
  }

  // Un cadeau = un VRAI goodie ajouté avec le code Corner de son palier (attribut de ligne _corner_promo_code).
  // C'est la remise Corner (Smart Discounts, côté Shopify) qui le passe à 0, et seulement si le palier est atteint :
  // un cadeau ne peut jamais être offert à tort, même ajouté à la main. Une paire de chaussettes achetée sans code reste payante.
  const GIFT_PROP = '_corner_promo_code';
  const giftCode = (item) => String((item && item.properties && item.properties[GIFT_PROP]) || '');
  const isGiftLine = (item) => giftCode(item) !== '';
  function extraProductIds(cfg) {
    return new Set((cfg.extraProductIds || []).map(Number));
  }
  const isExtraLine = (item, cfg, ids) => (ids || extraProductIds(cfg)).has(Number(item.product_id));

  // Base des paliers : total après remises, hors cadeaux et hors options payantes (boîte, pochon, protection).
  function base(cart, cfg) {
    const extras = extraProductIds(cfg);
    return cart.items.reduce((t, it) => (isGiftLine(it) || extras.has(Number(it.product_id)) ? t - it.final_line_price : t), cart.total_price);
  }
  function paidCount(cart, cfg) {
    const extras = extraProductIds(cfg);
    return cart.items.reduce((n, it) => (isGiftLine(it) || extras.has(Number(it.product_id)) ? n : n + it.quantity), 0);
  }

  // Options d'un palier qu'on peut réellement offrir : en stock.
  const offerable = (tier) => (tier.gifts || []).filter((g) => g.available !== false);

  // Paliers résolus pour la devise, triés par seuil ; un palier sans code Corner ou sans cadeau en stock disparaît.
  function resolveTiers(cfg) {
    return (cfg.tiers || [])
      .map((t) => Object.assign({}, t, { cents: thresholdCents(t.eur, t.fixed, cfg.currency, cfg.shopCurrency, cfg.rate), options: offerable(t) }))
      .filter((t) => t.code && t.cents != null && t.cents > 0 && t.options.length > 0)
      .sort((a, b) => a.cents - b.cents);
  }

  const giftLines = (cart) => cart.items.filter(isGiftLine);
  const isFreeLine = (it) => Number(it.final_line_price) === 0;

  // Choix de cadeau par palier : la mémoire d'abord, sinon lu sur la ligne qui porte le code du palier (autre appareil, mémoire effacée).
  function resolveChoices(cart, tiers, mem) {
    const choice = Object.assign({}, (mem && mem.choice) || {});
    tiers.forEach((t) => {
      if (choice[t.id]) return;
      const hit = giftLines(cart).find((it) => giftCode(it) === t.code && t.options.some((g) => Number(g.vid) === Number(it.variant_id)));
      if (hit) choice[t.id] = Number(hit.variant_id);
    });
    return choice;
  }

  // État complet, utilisé par la jauge, la liste des cadeaux et le rappel avant paiement.
  function state(cart, cfg, mem) {
    const tiers = resolveTiers(cfg);
    const b = base(cart, cfg);
    const choice = resolveChoices(cart, tiers, mem);
    const rows = tiers.map((t) => {
      const reached = b >= t.cents;
      const c = choice[t.id];
      const valid = c && c !== 'refused' && t.options.some((g) => Number(g.vid) === Number(c));
      let chosen = valid ? Number(c) : null;
      if (!chosen && reached && c !== 'refused' && c !== 'change' && t.auto) chosen = Number(t.options[0].vid);
      return { id: t.id, code: t.code, cents: t.cents, label: t.label, value: t.value, reveal: !!t.reveal, options: t.options, reached, refused: c === 'refused', chosen, missing: Math.max(0, t.cents - b) };
    });
    const next = rows.find((r) => !r.reached) || null;
    const ship = thresholdCents(null, cfg.shipping, cfg.currency, cfg.shopCurrency, cfg.rate);
    const pending = rows.filter((r) => r.reached && !r.chosen && !r.refused);
    return {
      base: b,
      paid: paidCount(cart, cfg),
      tiers: rows,
      next,
      allReached: rows.length > 0 && !next,
      shipping: ship == null ? null : { cents: ship, reached: b >= ship, missing: Math.max(0, ship - b) },
      pending, // cadeaux débloqués ni pris ni refusés : rappel avant paiement, boutons express cachés
      choice,
    };
  }

  // Ce qu'il faut écrire pour que le panier colle à l'état voulu :
  //   updates : { cléDeLigne: quantité } (cart/update.js), adds : lignes à ajouter (cart/add.js, avec le code du palier),
  //   blocked : codes dont la ligne reste payante alors que notre jauge dit « atteint » (Corner ne l'offre pas) :
  //   l'appelant les mémorise pour cette base, on ne les remet pas tant que le panier ne change pas (jamais de boucle).
  function plan(cart, cfg, mem) {
    const st = state(cart, cfg, mem);
    const blocked = (mem && mem.blocked) || {};
    const want = {};
    if (st.paid > 0) {
      st.tiers.forEach((r) => {
        if (r.reached && r.chosen && blocked[r.code] !== st.base) want[r.code + '|' + r.chosen] = { id: r.chosen, code: r.code };
      });
    }
    const updates = {};
    const newlyBlocked = [];
    const kept = new Set();
    giftLines(cart).forEach((it) => {
      const k = giftCode(it) + '|' + it.variant_id;
      if (want[k] && !kept.has(k)) {
        if (!isFreeLine(it)) { updates[it.key] = 0; newlyBlocked.push(giftCode(it)); return; }
        kept.add(k);
        if (it.quantity !== 1) updates[it.key] = 1;
        return;
      }
      updates[it.key] = 0;
    });
    const adds = Object.keys(want).filter((k) => !kept.has(k) && !newlyBlocked.includes(want[k].code))
      .map((k) => ({ id: Number(want[k].id), quantity: 1, properties: { [GIFT_PROP]: want[k].code } }));
    return { updates, adds, blocked: newlyBlocked, changed: Object.keys(updates).length > 0 || adds.length > 0, state: st };
  }

  // La cliente retire un cadeau : le palier qui l'avait choisi (son code s'il est connu) passe en « refusé » (on ne le remet pas).
  function refuse(cart, cfg, mem, vid, code) {
    const st = state(cart, cfg, mem);
    const choice = Object.assign({}, st.choice);
    st.tiers.forEach((r) => {
      if (Number(r.chosen) === Number(vid) && (!code || r.code === code)) choice[r.id] = 'refused';
    });
    return Object.assign({}, mem, { choice });
  }
  // « Change my gift » (Lisa 21/10) : le cadeau quitte le panier et le palier redemande un choix, sans compter comme un refus.
  function change(cart, cfg, mem, vid, code) {
    const st = state(cart, cfg, mem);
    const choice = Object.assign({}, st.choice);
    st.tiers.forEach((r) => {
      if (Number(r.chosen) === Number(vid) && (!code || r.code === code)) choice[r.id] = 'change';
    });
    return Object.assign({}, mem, { choice });
  }
  function choose(mem, tierId, vid) {
    return Object.assign({}, mem, { choice: Object.assign({}, (mem && mem.choice) || {}, { [tierId]: Number(vid) }) });
  }

  // Format monétaire déduit d'un échantillon rendu par Liquid ({{ 123456789 | money }} -> « 1 234 567,89 € »).
  function moneyFormatter(sample) {
    const s = String(sample || '');
    const m = s.match(/1(\D?)234(\D?)567(?:(\D)89)?/);
    if (!m) return (cents) => (cents / 100).toFixed(2);
    const [whole, thou, thou2, dec] = m;
    const pre = s.slice(0, m.index);
    const post = s.slice(m.index + whole.length);
    const sep = thou || thou2 || '';
    return (cents) => {
      const neg = cents < 0;
      const abs = Math.abs(Math.round(cents));
      const units = Math.floor(abs / 100);
      const rest = abs % 100;
      const grouped = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      const body = dec ? grouped + dec + String(rest).padStart(2, '0') : grouped;
      return (neg ? '-' : '') + pre + body + post;
    };
  }

  // Taille la plus présente au panier (options Taille / Size / Größe), pour proposer la bonne taille.
  function preferredSize(cart, names) {
    const keys = (names || ['taille', 'size', 'größe', 'grösse', 'grosse']).map((n) => n.toLowerCase());
    const count = {};
    cart.items.forEach((it) => {
      (it.options_with_values || []).forEach((o) => {
        if (keys.includes(String(o.name).toLowerCase())) count[o.value] = (count[o.value] || 0) + it.quantity;
      });
    });
    let best = null;
    Object.keys(count).forEach((k) => { if (best == null || count[k] > count[best]) best = k; });
    return best;
  }

  // Carte « combler l'écart » : la pièce la moins chère qui fait passer le prochain palier (à défaut la plus chère dispo).
  function gapPick(cards, missing, inCartProductIds) {
    const skip = new Set((inCartProductIds || []).map(Number));
    const list = cards.filter((c) => c.available && !skip.has(Number(c.pid)));
    if (!list.length || !(missing > 0)) return null;
    const enough = list.filter((c) => c.price >= missing).sort((a, b) => a.price - b.price);
    return enough[0] || list.slice().sort((a, b) => b.price - a.price)[0];
  }

  // Popup « One last thing » : UNE offre par écran, choisie ici.
  //  1 pièce payée                         -> 'pieces' : la 2e pièce qui débloque palier et livraison
  //  2 pièces ou plus, palier à portée      -> 'tier'   : une pièce de plus et le cadeau est à elle
  //  sinon, options non prises (boîte...)   -> 'pack'
  // « À portée » : il manque au plus reachPct du seuil du prochain palier (0,5 par défaut).
  function popupScreen(st, cards, inCartProductIds, opts) {
    const o = Object.assign({ reachPct: 0.5, packAvailable: false, after: null }, opts);
    if (o.after === 'refused') return null; // refus sur Pièces ou Palier : départ direct, pas d'écran Emballage
    if (o.after !== 'pieces' && o.after !== 'tier') {
      const target = st.next || (st.shipping && !st.shipping.reached ? { missing: st.shipping.missing, cents: st.shipping.cents } : null);
      if (target && st.paid >= 1) {
        const card = gapPick(cards, target.missing, inCartProductIds);
        if (card && st.paid === 1) return { screen: 'pieces', card, target };
        if (card && target.missing <= target.cents * o.reachPct) return { screen: 'tier', card, target };
      }
    }
    if (o.packAvailable) return { screen: 'pack' };
    return null;
  }

  // Groupe A (sans popup) / B (avec), tiré une fois par visiteuse : nombre aléatoire fourni (testable).
  const abGroup = (stored, rnd) => (stored === 'A' || stored === 'B' ? stored : rnd < 0.5 ? 'A' : 'B');

  return { popupScreen, abGroup, parseTable, thresholdCents, base, paidCount, resolveTiers, resolveChoices, state, plan, refuse, change, choose, moneyFormatter, preferredSize, gapPick, isGiftLine, isExtraLine, giftCode, GIFT_PROP };
});
